import { useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { businessCardApi } from "../api";
import { useSaveCardFields } from "../hooks";
import { CARD_FIELD_LABELS } from "../types";
import type { CardField, CardFieldAlign, CardFieldKey, CardFieldPage, CardTemplate } from "../types";

const DISPLAY_WIDTH = 520;
const MIN_BOX_PX = 14;

const ALL_KEYS: CardFieldKey[] = [
  "fullName", "jobTitle", "phone", "email", "department", "website", "qr",
];

/** Default pt size/style for a newly-added field, placed near the page's top-left. */
function defaultFieldFor(key: CardFieldKey, page: CardFieldPage): CardField {
  if (key === "qr") {
    return { page, key, x: 20, y: 20, width: 60, height: 60 };
  }
  return { page, key, x: 20, y: 100, width: 160, height: 16, fontSize: 10, align: "left" };
}

/** A field being edited, in on-screen CSS pixels (top-left origin) rather than PDF points. */
type EditableBox = {
  key: CardFieldKey;
  fontSize?: number | null;
  align?: CardFieldAlign | null;
  leftPx: number;
  topPx: number;
  widthPx: number;
  heightPx: number;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function fieldToBox(field: CardField, scale: number, displayHeight: number): EditableBox {
  return {
    key: field.key,
    fontSize: field.fontSize,
    align: field.align,
    widthPx: field.width * scale,
    heightPx: field.height * scale,
    leftPx: field.x * scale,
    topPx: displayHeight - (field.y + field.height) * scale,
  };
}

function boxToField(box: EditableBox, page: CardFieldPage, scale: number, displayHeight: number): CardField {
  const width = box.widthPx / scale;
  const height = box.heightPx / scale;
  return {
    page,
    key: box.key,
    width,
    height,
    x: box.leftPx / scale,
    y: (displayHeight - box.topPx) / scale - height,
    fontSize: box.key === "qr" ? undefined : box.fontSize,
    align: box.key === "qr" ? undefined : box.align,
  };
}

type Props = {
  template: CardTemplate;
  onSaved?: (template: CardTemplate) => void;
};

export function TemplateFieldEditor({ template, onSaved }: Props) {
  const [activePage, setActivePage] = useState<CardFieldPage>("front");
  const [fields, setFields] = useState<CardField[]>(template.fields);
  const [boxes, setBoxes] = useState<EditableBox[]>([]);
  const [selectedKey, setSelectedKey] = useState<CardFieldKey | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [newFieldKey, setNewFieldKey] = useState<CardFieldKey>("fullName");
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const saveFields = useSaveCardFields();

  const pageWidthPt = activePage === "front" ? template.frontWidthPt : (template.backWidthPt ?? template.frontWidthPt);
  const pageHeightPt = activePage === "front" ? template.frontHeightPt : (template.backHeightPt ?? template.frontHeightPt);
  const scale = DISPLAY_WIDTH / pageWidthPt;
  const displayHeight = pageHeightPt * scale;

  // Reset everything when a different template is opened.
  useEffect(() => {
    setFields(template.fields);
    setActivePage("front");
    setSelectedKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id]);

  // Re-derive the editable boxes whenever the active page (or the underlying
  // field data for it) changes — but NOT on every drag, since drags mutate
  // `boxes` directly rather than `fields`.
  useEffect(() => {
    setBoxes(
      fields.filter((f) => f.page === activePage).map((f) => fieldToBox(f, scale, displayHeight)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, template.id]);

  // Load the blank template page preview to draw the boxes over.
  useEffect(() => {
    let currentUrl: string | null = null;
    setPreviewLoading(true);
    businessCardApi
      .templatePreviewUrl(template.id, activePage)
      .then((url) => {
        currentUrl = url;
        setPreviewUrl(url);
      })
      .catch(() => setPreviewUrl(null))
      .finally(() => setPreviewLoading(false));
    return () => {
      if (currentUrl) window.URL.revokeObjectURL(currentUrl);
    };
  }, [template.id, activePage]);

  const availableKeys = useMemo(
    () => ALL_KEYS.filter((k) => !boxes.some((b) => b.key === k)),
    [boxes],
  );

  function updateBox(key: CardFieldKey, patch: Partial<EditableBox>) {
    setBoxes((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  }

  function addField() {
    if (boxes.some((b) => b.key === newFieldKey)) return;
    const field = defaultFieldFor(newFieldKey, activePage);
    setBoxes((prev) => [...prev, fieldToBox(field, scale, displayHeight)]);
    setSelectedKey(newFieldKey);
  }

  function removeSelected() {
    if (!selectedKey) return;
    setBoxes((prev) => prev.filter((b) => b.key !== selectedKey));
    setSelectedKey(null);
  }

  async function handleSave() {
    const committed = [
      ...fields.filter((f) => f.page !== activePage),
      ...boxes.map((b) => boxToField(b, activePage, scale, displayHeight)),
    ];
    setFields(committed);
    try {
      const updated = await saveFields.mutateAsync({ id: template.id, fields: committed });
      setToastMsg("Field positions saved.");
      onSaved?.(updated);
    } catch {
      setToastMsg("Failed to save field positions.");
    } finally {
      setTimeout(() => setToastMsg(null), 3000);
    }
  }

  const selectedBox = boxes.find((b) => b.key === selectedKey) ?? null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          {(["front", "back"] as CardFieldPage[])
            .filter((p) => p === "front" || template.hasBackPage)
            .map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setActivePage(p)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-bold capitalize ${
                  activePage === p
                    ? "border-emerald-500 bg-emerald-600 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {p}
              </button>
            ))}
        </div>

        <div className="flex items-center gap-2">
          {toastMsg && <span className="text-xs font-medium text-slate-500">{toastMsg}</span>}
          <button
            type="button"
            onClick={handleSave}
            disabled={saveFields.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saveFields.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save field positions
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-[1fr_220px]">
        {/* Canvas */}
        <div
          className="relative overflow-hidden rounded-xl border border-slate-300 bg-slate-100"
          style={{ width: DISPLAY_WIDTH, height: displayHeight }}
        >
          {previewLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          )}
          {previewUrl && (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img
              src={previewUrl}
              draggable={false}
              className="pointer-events-none absolute inset-0 h-full w-full select-none object-fill"
            />
          )}
          {boxes.map((box) => (
            <FieldBox
              key={box.key}
              box={box}
              selected={box.key === selectedKey}
              containerWidth={DISPLAY_WIDTH}
              containerHeight={displayHeight}
              onSelect={() => setSelectedKey(box.key)}
              onChange={(patch) => updateBox(box.key, patch)}
            />
          ))}
        </div>

        {/* Side panel */}
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-600">Add field</label>
            <div className="flex gap-1.5">
              <select
                value={newFieldKey}
                onChange={(e) => setNewFieldKey(e.target.value as CardFieldKey)}
                className="flex-1 rounded-lg border px-2 py-1 text-xs outline-none focus:border-emerald-500"
              >
                {availableKeys.map((k) => (
                  <option key={k} value={k}>{CARD_FIELD_LABELS[k]}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={addField}
                disabled={availableKeys.length === 0}
                className="rounded-lg border border-emerald-200 p-1.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {selectedBox ? (
            <div className="space-y-2 border-t pt-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700">{CARD_FIELD_LABELS[selectedBox.key]}</span>
                <button type="button" onClick={removeSelected} className="text-rose-500 hover:text-rose-700">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {selectedBox.key !== "qr" && (
                <>
                  <label className="block text-xs text-slate-500">
                    Font size (pt)
                    <input
                      type="number"
                      min={6}
                      max={48}
                      value={selectedBox.fontSize ?? 10}
                      onChange={(e) => updateBox(selectedBox.key, { fontSize: Number(e.target.value) })}
                      className="mt-1 w-full rounded-lg border px-2 py-1 text-xs outline-none focus:border-emerald-500"
                    />
                  </label>
                  <label className="block text-xs text-slate-500">
                    Alignment
                    <select
                      value={selectedBox.align ?? "left"}
                      onChange={(e) => updateBox(selectedBox.key, { align: e.target.value as CardFieldAlign })}
                      className="mt-1 w-full rounded-lg border px-2 py-1 text-xs outline-none focus:border-emerald-500"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </label>
                </>
              )}
              <p className="text-[11px] text-slate-400">Drag to move, drag the corner handle to resize.</p>
            </div>
          ) : (
            <p className="border-t pt-3 text-[11px] text-slate-400">
              Select a field box to edit its font size and alignment, or add a new field above.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Draggable / resizable box ──────────────────────────────────────────────

function FieldBox({
  box,
  selected,
  containerWidth,
  containerHeight,
  onSelect,
  onChange,
}: {
  box: EditableBox;
  selected: boolean;
  containerWidth: number;
  containerHeight: number;
  onSelect: () => void;
  onChange: (patch: Partial<EditableBox>) => void;
}) {
  const dragRef = useRef<{ startX: number; startY: number; startLeft: number; startTop: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);

  function handleDragDown(e: ReactPointerEvent<HTMLDivElement>) {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startLeft: box.leftPx, startTop: box.topPx };
    onSelect();
  }
  function handleDragMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    onChange({
      leftPx: clamp(dragRef.current.startLeft + dx, 0, containerWidth - box.widthPx),
      topPx: clamp(dragRef.current.startTop + dy, 0, containerHeight - box.heightPx),
    });
  }
  function handleDragUp() {
    dragRef.current = null;
  }

  function handleResizeDown(e: ReactPointerEvent<HTMLDivElement>) {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeRef.current = { startX: e.clientX, startY: e.clientY, startWidth: box.widthPx, startHeight: box.heightPx };
  }
  function handleResizeMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!resizeRef.current) return;
    const dx = e.clientX - resizeRef.current.startX;
    const dy = e.clientY - resizeRef.current.startY;
    onChange({
      widthPx: clamp(resizeRef.current.startWidth + dx, MIN_BOX_PX, containerWidth - box.leftPx),
      heightPx: clamp(resizeRef.current.startHeight + dy, MIN_BOX_PX, containerHeight - box.topPx),
    });
  }
  function handleResizeUp() {
    resizeRef.current = null;
  }

  return (
    <div
      onPointerDown={handleDragDown}
      onPointerMove={handleDragMove}
      onPointerUp={handleDragUp}
      style={{ position: "absolute", left: box.leftPx, top: box.topPx, width: box.widthPx, height: box.heightPx }}
      className={`flex cursor-move select-none items-center justify-center rounded border-2 text-[10px] font-bold ${
        selected ? "border-emerald-500 bg-emerald-500/15 text-emerald-800" : "border-indigo-400 bg-indigo-400/15 text-indigo-800"
      }`}
    >
      <span className="truncate px-1">{CARD_FIELD_LABELS[box.key]}</span>
      <div
        onPointerDown={handleResizeDown}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeUp}
        className="absolute -bottom-1 -right-1 h-3 w-3 cursor-se-resize rounded-sm border border-white bg-slate-600"
      />
    </div>
  );
}
